import { AuthService } from './authService';
import { extractApiError } from '../utils/apiError';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

export interface StcTransDto {
  id?: number;
  companyNo: string;
  lineID: number;
  lineType: number;
  orderNo?: number;
  lineNo?: number;
  locationName?: string;
  quantity?: number;
  transDt?: Date;
  toLocationName?: string;
  warehouse?: string;
  toWarehouse?: string;
  operatorID: string;
  profileID: string;
  exportedYesNo?: boolean;
  batchID: string;
  lineStatus: number;
  itemNo: string;
  itemUnitCode?: string;
  itemBarCode?: string;
  description1?: string;
  itemBatchNo?: string;
  itemBatchExpDate?: Date;
  itemSerialNo?: string;
  erpLinkName?: string;
  shipmentNo?: string;
  quantityOrigin?: number;
  itemUnitTxt?: string;
  lineTypeOrigin?: number;
  price?: number;
  currency?: string;
  reasonCode?: number;
  transType?: number;
  sscc?: string;
  quantityCorrected?: number;
  freeField1?: string;
  freeField2?: string;
  freeField3?: string;
  appFreeField1?: string;
  appFreeField2?: string;
  quantityGross?: number;
  transitPackagingID?: number;
  clientID?: string;
  stcBoxNo?: number;
  grShipmentNo?: number;
  externalSystemID?: number;
  externalSystemStatus?: number;
  itemType?: number;
  orderNoERP?: string;
  lineNoERP?: number;
  created?: Date;
  lastUpdate?: Date;
  createdBy?: string;
  updatedBy?: string;
  statusText?: string;
  transTypeText?: string;
}

export interface StcTransOrderDto {
  companyNo: string;
  batchID: string;
  operatorID?: string;
  orderDate?: Date;
  description1?: string;
  orderStatus: number;
  profileID?: string;
  orderType: number;
  warehouse?: string;
  lineTypeOrigin?: number;
  startWorkDateTime?: Date;
  stopWorkDateTime?: Date;
  statID?: number;
  customID?: string;
  lineSortingFieldID?: number;
  created?: Date;
  lastUpdate?: Date;
  createdBy?: string;
  updatedBy?: string;
  statusText: string;
  transactionCount: number;
}

export interface CreateStcTransRequest {
  batchID: string;
  // LineID fjernet - genereres automatisk i backend
  lineType?: number;
  orderNo?: number;
  lineNo?: number;
  locationName?: string;
  quantity?: number;
  transDt?: Date;
  toLocationName?: string;
  warehouse?: string;
  toWarehouse?: string;
  operatorID?: string;
  profileID?: string;
  exportedYesNo?: boolean;
  lineStatus?: number;
  itemNo: string;
  itemUnitCode?: string;
  itemBarCode?: string;
  description1?: string;
  itemBatchNo?: string;
  itemBatchExpDate?: Date;
  itemSerialNo?: string;
  erpLinkName?: string;
  shipmentNo?: string;
  quantityOrigin?: number;
  itemUnitTxt?: string;
  lineTypeOrigin?: number;
  price?: number;
  currency?: string;
  reasonCode?: number;
  transType?: number;
  sscc?: string;
  quantityCorrected?: number;
  freeField1?: string;
  freeField2?: string;
  freeField3?: string;
  appFreeField1?: string;
  appFreeField2?: string;
  quantityGross?: number;
  transitPackagingID?: number;
  clientID?: string;
  stcBoxNo?: number;
  grShipmentNo?: number;
  externalSystemID?: number;
  externalSystemStatus?: number;
  itemType?: number;
  orderNoERP?: string;
  lineNoERP?: number;
}

export interface CreateStcTransOrderRequest {
  batchID: string;
  operatorID?: string;
  orderDate?: Date;
  description1?: string;
  orderStatus?: number;
  profileID?: string;
  orderType?: number;
  warehouse?: string;
  lineTypeOrigin?: number;
  startWorkDateTime?: Date;
  statID?: number;
  customID?: string;
  lineSortingFieldID?: number;
}

export interface CreateStcTransBulkRequest {
  transactions: CreateStcTransRequest[];
}

export interface CreateStcTransBulkResponse {
  success: boolean;
  errorMessage?: string;
  createdTransactions: StcTransDto[];
  successCount: number;
}

export interface FinishReceivingAndPutAwayRequest {
  orderNo: number;
  operatorID?: string;
}

export interface FinishReceivingAndPutAwayResponse {
  success: boolean;
  receivingFinished: boolean;
  putAwayTasksGenerated: boolean;
  putAwayTasksCreated: number;
  stcOrderNo?: number;
  messages: string[];
  errorMessage?: string;
  /** Items that are missing ItemBinSize configuration (bulk pre-validation) */
  itemsMissingBinConfig?: string[];
  /** True when finish was blocked by the put-away bin resolution dry-run (nothing was finished) */
  putAwayBlocked?: boolean;
  /** Items that block the finish operation because put-away bin resolution failed */
  putAwayBlockedItems?: PutAwayBlockedItem[];
}

/** An item for which put-away bin resolution failed (pre-flight dry-run or retry) */
export interface PutAwayBlockedItem {
  itemNo: string;
  itemText?: string;
  reason?: string;
}

/** A baseline row (StcIntRelocation) still waiting for put-away generation */
export interface PendingPutAwayItem {
  relID: string;
  itemNo: string;
  itemText?: string;
  quantity: number;
  binCode: string;
  warehouseID: string;
}

export interface PendingPutAwayResponse {
  batchID: string;
  pendingCount: number;
  items: PendingPutAwayItem[];
}

export interface RetryPutAwayResponse {
  success: boolean;
  batchID: string;
  resolvedCount: number;
  remainingCount: number;
  stcOrderNo?: number;
  stillFailingItems: PutAwayBlockedItem[];
  messages: string[];
  errorMessage?: string;
  processedAt: Date;
}

/**
 * Result of finishing receiving for a single operator in multi-operator scenarios.
 * Used for Modus 2 (Åpent mottak) and Modus 4 (Bilmottak) where multiple operators
 * can work on the same BatchID simultaneously.
 */
/**
 * Result of closing a sorting box (partial finish of lines grouped by StcBoxNo)
 */
export interface CloseSortingBoxResult {
  success: boolean;
  errorMessage?: string;
  stcBoxNo: number;
  linesFinished: number;
  remainingOpenLines: number;
  batchId: string;
  processedAt: Date;
}

export interface FinishOperatorReceivingResult {
  success: boolean;
  errorMessage?: string;
  linesFinished: number;
  linesAlreadyFinished: number;
  otherOperatorsHaveActiveLines: boolean;
  otherOperatorsActiveLinesCount: number;
  batchId: string;
  operatorId: string;
  processedAt: Date;
  currentOrderStatus: number;
  putAwayTaskIds: number[];
}

class StcTransService {
  
  /**
   * Henter alle mottakslinjer for en spesifikk batch
   */
  async getTransactionsByBatch(batchId: string, bustCache: boolean = false): Promise<StcTransDto[]> {
    const encodedBatchId = encodeURIComponent(batchId);
    // Add cache-busting parameter if requested
    const cacheBuster = bustCache ? `?_t=${Date.now()}` : '';

    // Add cache-prevention headers when cache-busting
    const options = bustCache ? {
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      }
    } : undefined;

    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/stocktrans/${encodedBatchId}/transactions${cacheBuster}`,
      options
    );

    if (!response.ok) {
      throw new Error(`Failed to get transactions for batch ${batchId}: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Oppretter en ny mottakslinje
   */
  async createTransaction(batchId: string, request: CreateStcTransRequest): Promise<StcTransDto> {
    const encodedBatchId = encodeURIComponent(batchId);

    try {
      const response = await AuthService.authenticatedPost(
        `${API_BASE_URL}/stocktrans/${encodedBatchId}/transactions`,
        request
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(extractApiError(errorData, response, 'Failed to create transaction'));
      }

      return await response.json();
    } catch (error) {
      console.error('Detaljert feil ved opprettelse av transaksjon:', error);
      console.error('Request data:', request);
      throw error;
    }
  }

  /**
   * Oppretter flere mottakslinjer samtidig (bulk)
   */
  async createTransactionsBulk(batchId: string, transactions: CreateStcTransRequest[]): Promise<StcTransDto[]> {
    const encodedBatchId = encodeURIComponent(batchId);

    try {
      const bulkRequest: CreateStcTransBulkRequest = {
        transactions: transactions
      };

      const response = await AuthService.authenticatedPost(
        `${API_BASE_URL}/stocktrans/${encodedBatchId}/transactions/bulk`,
        bulkRequest
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(extractApiError(errorData, response, 'Failed to create transactions in bulk'));
      }

      const bulkResponse: CreateStcTransBulkResponse = await response.json();

      if (!bulkResponse.success) {
        throw new Error(bulkResponse.errorMessage || 'Bulk creation failed');
      }

      return bulkResponse.createdTransactions;
    } catch (error) {
      console.error('Detaljert feil ved bulk-opprettelse av transaksjoner:', error);
      console.error('Request data:', transactions);
      throw error;
    }
  }

  /**
   * Oppdaterer batch-informasjon for en eksisterende transaksjon
   */
  async updateBatchInfo(batchId: string, lineId: number, batchInfo: {
    itemBatchNo?: string;
    itemBatchExpDate?: Date;
  }): Promise<StcTransDto> {
    const encodedBatchId = encodeURIComponent(batchId);
    
    const response = await AuthService.authenticatedPatch(
      `${API_BASE_URL}/stocktrans/${encodedBatchId}/transactions/${lineId}/batch-info`,
      batchInfo
    );
    
    if (!response.ok) {
      // Hvis PATCH ikke er støttet ennå, fall tilbake til delete/create
      if (response.status === 404 || response.status === 405) {
        console.warn('PATCH not supported, falling back to delete/create method');
        throw new Error('UPDATE_NOT_SUPPORTED');
      }
      throw new Error(`Failed to update batch info: ${response.status} ${response.statusText}`);
    }
    
    return await response.json();
  }

  /**
   * Oppdaterer en komplett transaksjon
   */
  async updateTransaction(batchId: string, lineId: number, request: CreateStcTransRequest): Promise<StcTransDto> {
    const encodedBatchId = encodeURIComponent(batchId);
    
    const response = await AuthService.authenticatedPut(
      `${API_BASE_URL}/stocktrans/${encodedBatchId}/transactions/${lineId}`,
      request
    );
    
    if (!response.ok) {
      // Hvis PUT ikke er støttet ennå, fall tilbake til delete/create
      if (response.status === 404 || response.status === 405) {
        console.warn('PUT not supported, falling back to delete/create method');
        throw new Error('UPDATE_NOT_SUPPORTED');
      }
      throw new Error(`Failed to update transaction: ${response.status} ${response.statusText}`);
    }
    
    return await response.json();
  }

  /**
   * Sletter en mottakslinje
   */
  async deleteTransaction(batchId: string, lineId: number): Promise<void> {
    const encodedBatchId = encodeURIComponent(batchId);
    const response = await AuthService.authenticatedDelete(
      `${API_BASE_URL}/stocktrans/${encodedBatchId}/transactions/${lineId}`
    );
    
    if (!response.ok) {
      throw new Error(`Failed to delete transaction: ${response.status} ${response.statusText}`);
    }
  }

  /**
   * Oppretter en ny mottaksordre
   */
  async createTransOrder(request: CreateStcTransOrderRequest): Promise<StcTransOrderDto> {
    // Legg til startWorkDateTime hvis det ikke allerede er satt
    const requestWithStartTime = {
      ...request,
      startWorkDateTime: request.startWorkDateTime || new Date()
    };
    
    const response = await AuthService.authenticatedPost(
      `${API_BASE_URL}/stocktrans`,
      requestWithStartTime
    );
    
    if (!response.ok) {
      throw new Error(`Failed to create stock trans order: ${response.status} ${response.statusText}`);
    }
    
    return await response.json();
  }

  /**
   * Henter en mottaksordre
   */
  async getTransOrder(batchId: string): Promise<StcTransOrderDto> {
    const encodedBatchId = encodeURIComponent(batchId);
    const response = await AuthService.authenticatedGet(`${API_BASE_URL}/stocktrans/${encodedBatchId}`);
    
    if (!response.ok) {
      throw new Error(`Failed to get stock trans order ${batchId}: ${response.status} ${response.statusText}`);
    }
    
    return await response.json();
  }

  /**
   * Henter mottaksordre basert på OrderNo
   */
  async getTransOrderByOrderNo(orderNo: number): Promise<StcTransOrderDto | null> {
    const response = await AuthService.authenticatedGet(`${API_BASE_URL}/stocktrans/order/${orderNo}`);
    
    if (response.status === 404) {
      return null; // Ikke funnet
    }
    
    if (!response.ok) {
      throw new Error(`Failed to get stock trans order by OrderNo ${orderNo}: ${response.status} ${response.statusText}`);
    }
    
    return await response.json();
  }

  /**
   * Genererer en unik batch-ID basert på ordrenummer og timestamp
   */
  generateBatchId(orderNo: number): string {
    // Bruk en enklere timestamp-format som er URL-safe
    const now = new Date();
    const timestamp = now.getFullYear().toString() + 
                     (now.getMonth() + 1).toString().padStart(2, '0') + 
                     now.getDate().toString().padStart(2, '0') + 
                     now.getHours().toString().padStart(2, '0') + 
                     now.getMinutes().toString().padStart(2, '0') + 
                     now.getSeconds().toString().padStart(2, '0');
    return `RCV_${orderNo}_${timestamp}`;
  }

  /**
   * Henter eksisterende BatchID for en ordre eller oppretter ny hvis ingen finnes
   */
  async getOrCreateBatchId(orderNo: number): Promise<string> {
    try {
      // Prøv å hente eksisterende mottaksordre
      const existingTransOrder = await this.getTransOrderByOrderNo(orderNo);
      
      if (existingTransOrder) {
        console.log(`Eksisterende BatchID funnet for OrderNo ${orderNo}: ${existingTransOrder.batchID}`);
        return existingTransOrder.batchID;
      }
    } catch (err) {
      console.log(`Ingen eksisterende mottaksordre funnet for OrderNo ${orderNo}, genererer ny BatchID`);
    }
    
    // Generer ny BatchID hvis ingen eksisterer
    const newBatchId = this.generateBatchId(orderNo);
    console.log(`Ny BatchID generert for OrderNo ${orderNo}: ${newBatchId}`);
    return newBatchId;
  }

  /**
   * Sjekker om en mottaksordre (BatchID) eksisterer
   */
  async batchIdExists(batchId: string): Promise<boolean> {
    try {
      await this.getTransOrder(batchId);
      return true;
    } catch (err) {
      return false;
    }
  }

  /**
   * Henter alle BatchID-er for en spesifikk ordre
   */
  async getAllBatchIdsForOrder(orderNo: number): Promise<string[]> {
    try {
      const existingTransOrder = await this.getTransOrderByOrderNo(orderNo);
      return existingTransOrder ? [existingTransOrder.batchID] : [];
    } catch (err) {
      console.log(`Ingen BatchID-er funnet for OrderNo ${orderNo}`);
      return [];
    }
  }

  /**
   * Oppretter mottaksordre hvis den ikke eksisterer
   */
  async ensureTransOrderExists(batchId: string, orderData: any): Promise<void> {
    try {
      await this.getTransOrder(batchId);
      console.log(`Mottaksordre eksisterer allerede for BatchID: ${batchId}`);
    } catch (err) {
      console.log(`Mottaksordre eksisterer ikke for BatchID: ${batchId}, oppretter den`);
      
      await this.createTransOrder({
        batchID: batchId,
        description1: `Varemottak for ordre ${orderData.orderNoERP}`,
        orderStatus: 20, // 20 = Under arbeid
        orderType: 0, // Mottak
        warehouse: orderData.warehouse || 'MAIN',
        lineTypeOrigin: 0
      });
      
      console.log(`Mottaksordre opprettet for BatchID: ${batchId}`);
    }
  }

  /**
   * Sjekker om et mottak kan avsluttes
   */
  async canFinishReceiving(batchId: string): Promise<{ canFinish: boolean; message: string }> {
    const encodedBatchId = encodeURIComponent(batchId);
    const response = await AuthService.authenticatedGet(`${API_BASE_URL}/stocktrans/${encodedBatchId}/can-finish-receiving`);
    
    if (!response.ok) {
      throw new Error(`Failed to check if receiving can be finished: ${response.status} ${response.statusText}`);
    }
    
    return await response.json();
  }

  /**
   * Avslutter et mottak
   */
  async finishReceiving(batchId: string): Promise<void> {
    const encodedBatchId = encodeURIComponent(batchId);
    const response = await AuthService.authenticatedPost(
      `${API_BASE_URL}/stocktrans/${encodedBatchId}/finish-receiving`,
      {}
    );
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(extractApiError(errorData, response, 'Failed to finish receiving'));
    }
  }

  /**
   * Starter et mottak
   */
  async startReceiving(batchId: string): Promise<void> {
    const encodedBatchId = encodeURIComponent(batchId);
    const response = await AuthService.authenticatedPost(
      `${API_BASE_URL}/stocktrans/${encodedBatchId}/start-receiving`,
      {}
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(extractApiError(errorData, response, 'Failed to start receiving'));
    }
  }

  /**
   * Avslutter mottak og genererer plassering-oppgaver automatisk
   */
  async finishReceivingAndPutAway(
    batchId: string,
    request: FinishReceivingAndPutAwayRequest
  ): Promise<FinishReceivingAndPutAwayResponse> {
    const encodedBatchId = encodeURIComponent(batchId);
    const response = await AuthService.authenticatedPost(
      `${API_BASE_URL}/stocktrans/${encodedBatchId}/finish-and-putaway`,
      request
    );

    if (!response.ok) {
      // Parse the error response - backend returns structured FinishReceivingAndPutAwayResponse even on 400
      const errorData: FinishReceivingAndPutAwayResponse = await response.json().catch(() => ({
        success: false,
        receivingFinished: false,
        putAwayTasksGenerated: false,
        putAwayTasksCreated: 0,
        messages: [],
        errorMessage: 'Unknown error'
      }));

      // If backend returned itemsMissingBinConfig or a put-away dry-run block (409),
      // return the structured response instead of throwing - so the caller can show
      // a detailed error message / open the config modal for the blocking items
      if (errorData.itemsMissingBinConfig && errorData.itemsMissingBinConfig.length > 0) {
        return errorData;
      }
      if (errorData.putAwayBlocked && errorData.putAwayBlockedItems && errorData.putAwayBlockedItems.length > 0) {
        return errorData;
      }

      throw new Error(extractApiError(errorData, response, 'Failed to finish receiving and put away'));
    }

    return await response.json();
  }

  /**
   * Henter gjenstående grunnlagsrader (StcIntRelocation LineStatus=20) for en mottaksbatch.
   * Brukes til å oppdage at plassering-generering feilet for én eller flere varer etter
   * at batchen ble ferdigstilt.
   */
  async getPendingPutAway(batchId: string): Promise<PendingPutAwayResponse> {
    const encodedBatchId = encodeURIComponent(batchId);
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/stocktrans/${encodedBatchId}/pending-putaway`
    );

    if (!response.ok) {
      throw new Error(`Failed to get pending put-away: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Re-kjører plassering-genereringen for gjenstående grunnlagsrader på en (lukket) mottaksbatch.
   * Soft-fail per vare: varer som fortsatt ikke kan løses rapporteres i stillFailingItems.
   */
  async retryPutAway(batchId: string): Promise<RetryPutAwayResponse> {
    const encodedBatchId = encodeURIComponent(batchId);
    const response = await AuthService.authenticatedPost(
      `${API_BASE_URL}/stocktrans/${encodedBatchId}/retry-putaway`,
      {}
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(extractApiError(errorData, response, 'Failed to retry put-away generation'));
    }

    return await response.json();
  }

  /**
   * Henter operatørens aktive frie mottak for auto-gjenoppta, eller null hvis ingen finnes.
   * Operatøren eier mottaket — backend bruker innlogget operatør fra token (ingen param).
   */
  async getActiveFreeReceiving(): Promise<StcTransOrderDto | null> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/stocktrans/free-receiving/active`
    );

    if (response.status === 204) {
      return null; // Ingen aktivt fritt mottak for operatøren
    }

    if (!response.ok) {
      throw new Error(`Failed to get active free receiving: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Henter eksisterende eller oppretter ny BatchID for fritt mottak.
   * Auto-gjenoppta: hvis operatøren allerede har et aktivt fritt mottak (ikke avsluttet),
   * gjenbrukes det. Kun ett aktivt fritt mottak per operatør — nytt opprettes først etter avslutning.
   */
  async getOrCreateBatchIdForOpenReceiving(): Promise<string> {
    // 1) Forsøk å gjenoppta operatørens aktive frie mottak.
    const existing = await this.getActiveFreeReceiving();
    if (existing?.batchID) {
      console.log('Resuming active free receiving batch:', existing.batchID);
      return existing.batchID;
    }

    // 2) Ingen aktivt — opprett nytt. Format: OPEN-{timestamp}-{random}
    const timestamp = new Date().toISOString().replace(/[-:T.Z]/g, '').slice(0, 14);
    const random = Math.random().toString(36).substring(2, 6).toUpperCase();
    const batchId = `OPEN-${timestamp}-${random}`;

    console.log('Generated new BatchID for open receiving:', batchId);

    // Create the StcTransOrder on the backend
    const response = await AuthService.authenticatedPost(
      `${API_BASE_URL}/stocktrans`,
      {
        batchID: batchId,
        orderType: 0, // 0 = Mottak
        orderStatus: 20, // 20 = Under arbeid
        description1: 'Fritt mottak'
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to create open receiving batch: ${response.status} ${response.statusText}`);
    }

    return batchId;
  }

  // ========================================================================
  // Serial number support methods (ItemType=6)
  // ========================================================================

  /**
   * Sjekker om et serienummer allerede eksisterer for en vare (globalt unikt per vare)
   * Brukes for sanntids duplikatvalidering under mottak
   */
  async checkSerialNumberExists(itemNo: string, serialNo: string): Promise<boolean> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/stocktrans/serialnumber/exists?itemNo=${encodeURIComponent(itemNo)}&serialNo=${encodeURIComponent(serialNo)}`
    );

    if (!response.ok) {
      throw new Error(`Failed to check serial number: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();
    return result.exists;
  }

  /**
   * Henter alle registrerte serienumre for en vare (på tvers av alle lokasjoner)
   */
  async getSerialNumbersForItem(itemNo: string): Promise<{ serialNo: string; binCode: string; warehouseId: string }[]> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/stocktrans/serialnumbers/${encodeURIComponent(itemNo)}`
    );

    if (!response.ok) {
      throw new Error(`Failed to get serial numbers: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Henter serienumre for en vare på en spesifikk lokasjon
   */
  async getSerialNumbersAtBin(itemNo: string, warehouseId: string, binCode: string): Promise<{ serialNo: string; binCode: string; warehouseId: string }[]> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/stocktrans/serialnumbers/${encodeURIComponent(itemNo)}/location/${encodeURIComponent(warehouseId)}/${encodeURIComponent(binCode)}`
    );

    if (!response.ok) {
      throw new Error(`Failed to get serial numbers at bin: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Henter batcher for en vare på en spesifikk lokasjon
   */
  async getBatchesAtBin(itemNo: string, warehouseId: string, binCode: string): Promise<{ batchNo: string; expiryDate: string | null; quantity: number; binCode: string; warehouseId: string }[]> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/stocktrans/batches/${encodeURIComponent(itemNo)}/location/${encodeURIComponent(warehouseId)}/${encodeURIComponent(binCode)}`
    );

    if (!response.ok) {
      throw new Error(`Failed to get batches at bin: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Avslutter mottak for gjeldende operatør (multi-operatør støtte).
   * Kun avslutter operatørens egne linjer - andre operatører kan fortsette å jobbe.
   * Brukes for Modus 2 (Åpent mottak) og Modus 4 (Bilmottak).
   */
  async finishOperatorReceiving(batchId: string): Promise<FinishOperatorReceivingResult> {
    const encodedBatchId = encodeURIComponent(batchId);
    const response = await AuthService.authenticatedPost(
      `${API_BASE_URL}/stocktrans/${encodedBatchId}/finish-operator-receiving`,
      {}
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        success: false,
        errorMessage: 'Unknown error',
        linesFinished: 0,
        linesAlreadyFinished: 0,
        otherOperatorsHaveActiveLines: false,
        otherOperatorsActiveLinesCount: 0,
        batchId: batchId,
        operatorId: '',
        processedAt: new Date(),
        currentOrderStatus: 0,
        putAwayTaskIds: []
      }));
      throw new Error(extractApiError(errorData, response, 'Failed to finish operator receiving'));
    }

    return await response.json();
  }

  /**
   * Close a sorting box - partial finish of all StcTrans lines with the given StcBoxNo.
   * Sets LineStatus=50, creates StcIntRelocation, ItemBins and StcTransaction records.
   */
  async closeSortingBox(batchId: string, stcBoxNo: number): Promise<CloseSortingBoxResult> {
    const encodedBatchId = encodeURIComponent(batchId);
    const response = await AuthService.authenticatedPost(
      `${API_BASE_URL}/stocktrans/${encodedBatchId}/close-sorting-box`,
      { stcBoxNo }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        success: false,
        errorMessage: 'Unknown error',
        stcBoxNo,
        linesFinished: 0,
        remainingOpenLines: 0,
        batchId: batchId,
        processedAt: new Date()
      }));
      throw new Error(extractApiError(errorData, response, 'Failed to close sorting box'));
    }

    return await response.json();
  }

  /**
   * Get the next available StcBoxNo for a warehouse (used to reserve box numbers in sorting mode)
   */
  async getNextStcBoxNo(warehouse: string, stcBoxTypeID?: string): Promise<number> {
    let url = `${API_BASE_URL}/stocktrans/next-stc-box-no?warehouse=${encodeURIComponent(warehouse)}`;
    if (stcBoxTypeID) {
      url += `&stcBoxTypeID=${encodeURIComponent(stcBoxTypeID)}`;
    }
    const response = await AuthService.authenticatedGet(url);

    if (!response.ok) {
      throw new Error(`Failed to get next StcBoxNo: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  }
}

export const stcTransService = new StcTransService();
